// ============================================================================
// DOCOSPOT - MAIN APPLICATION FILE (MODULAR VERSION)
// ============================================================================
// This file imports all feature modules and handles core initialization

// Import core modules
import { API_BASE_URL } from './core/config.js';
import { t, setLanguage, translateAlarmReason } from './core/i18n.js';
import { auth } from './core/auth.js';
import { apiCall } from './core/api.js';
import { showError, showSuccess, showConfirmDialog, showAlertDialog } from './core/ui.js';

// Import utilities
import { getCurrentUser, formatDateTime, autoLogout, logout, handleApiError } from './utils/helpers.js';

// Import feature modules
import { updateNavigation, updateActiveNavigation, updatePageTitle, loadCurrentUser, toggleSetupMenu } from './modules/navigation.js';
import { showDashboard, toggleDashboardAccountItem } from './modules/dashboard.js';
import accountsModule from './modules/accounts.js';
const { showAccounts, toggleAccountItem, showCreateAccountModal, loadAccountsData, editAccount, viewAccountUsersFiltered, createAccount, updateAccount, toggleParentAccountTreeItem, selectParentAccount, toggleEditParentAccountTreeItem, selectEditParentAccount, handleAccountSearchInput, handleAccountSearchFocus, handleAccountSearchBlur, selectAccountFromSearch, clearAccountSearch } = accountsModule;
import usersModule from './modules/users.js';
import usersModalsModule from './modules/users-modals.js';
const { showUsers, loadUsersData, clearFilters, sortUsersTable } = usersModule;
const { showCreateUserModal, createUser, editUser, updateUser, deleteUser, resendInvitation, toggleAccountTreeItem, selectAccount, selectEditAccount } = usersModalsModule;
import locationsModule from './modules/locations.js';
import locationsModalsModule from './modules/locations-modals.js';
import locationEditModule from './modules/location-edit.js';
const { showLocations, loadLocationsData, clearLocationsFilters, toggleLocationsAccountFilterExpanded, clearLocationsAccountFilter, toggleLocationsAccountFilterTreeItem } = locationsModule;
const { showCreateLocationModal, createLocation, updateLocation, toggleLocationAccountTreeItem, selectLocationAccount, selectEditLocationAccount } = locationsModalsModule;
const { editLocation, loadAvailableSensorsForLocation } = locationEditModule;
import { initAssets, showAssets } from './modules/assets.js';
import './modules/assets-modals.js';
import { showMyAssets, showAssetInsightsTab } from './modules/asset-insights.js';
import { showTimeTracking, showTimeTrackingTab, loadTimeTrackingData } from './modules/time-tracking.js';
import './modules/time-budget.js'; // Import for window exports
import { showSensors, showSetupSensors } from './modules/sensors.js';
import './modules/sensors-modals.js'; // Import for window exports
import { showAlarms } from './modules/alarms.js';
import './modules/alarms-modals.js'; // Import for window exports
import { showAlarmTemplates, showSetupAlarms } from './modules/alarm-templates.js';
import './modules/alarm-templates-modals.js'; // Import for window exports
import { showReports } from './modules/reports.js';
import { showReportTemplates } from './modules/report-templates.js';
import { showSettings } from './modules/settings.js';
import { showMyLocations, showMyLocationsTab } from './modules/insights.js';
import { showProfile } from './modules/profile.js';
import { showCategories, showAnalytics, showFavorites, showGroups } from './modules/legacy.js';
import { showTasks, switchTasksTab } from './modules/tasks.js';

// Import WebSocket client
import { wsClient } from './core/websocket.js';

console.log('🔧 app.js is loading...');

// ============================================================================
// DATE RANGE PICKER
// ============================================================================

function createRangePicker(config) {
    const {
        buttonId,
        displayId,
        popupId,
        fromInputId,
        toInputId,
        onApply,
        singleDate = false,
        buttonText = 'Select date range'
    } = config;
    
    const state = {
        currentMonth: new Date(),
        startDate: null,
        endDate: null,
        isOpen: false,
        singleDate: singleDate
    };
    
    // Create toggle method for the returned object
    const toggleFn = function() {
        const popup = document.getElementById(popupId);
        if (!popup) return;
        
        if (state.isOpen) {
            popup.style.opacity = '0';
            popup.style.transform = 'translateY(-8px)';
            setTimeout(() => {
                popup.classList.add('hidden');
                popup.style.opacity = '';
                popup.style.transform = '';
            }, 150);
            state.isOpen = false;
            return;
        }
        
        const fromInput = document.getElementById(fromInputId);
        const toInput = document.getElementById(toInputId);
        
        if (fromInput?.value) {
            state.startDate = new Date(fromInput.value + 'T00:00:00');
            state.startDate.setHours(0, 0, 0, 0);
        }
        if (!singleDate && toInput?.value) {
            state.endDate = new Date(toInput.value + 'T00:00:00');
            state.endDate.setHours(0, 0, 0, 0);
        }
        
        if (state.startDate) {
            state.currentMonth = new Date(state.startDate);
        } else {
            state.currentMonth = new Date();
        }
        
        const button = document.getElementById(buttonId);
        if (button) {
            const rect = button.getBoundingClientRect();
            popup.style.left = `${rect.left}px`;
            popup.style.top = `${rect.bottom + window.scrollY + 4}px`;
        }
        
        state.isOpen = true;
        renderFn();
        
        popup.style.opacity = '0';
        popup.style.transform = 'translateY(-8px)';
        popup.style.transition = 'opacity 150ms ease-out, transform 150ms ease-out';
        popup.classList.remove('hidden');
        
        requestAnimationFrame(() => {
            popup.style.opacity = '1';
            popup.style.transform = 'translateY(0)';
        });
    };
    
    const updateDisplayFn = function() {
        const display = document.getElementById(displayId);
        if (!display) return;
        
        const { startDate, endDate } = state;
        const formatDate = (d) => {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
        };
        
        if (singleDate && startDate) {
            display.textContent = formatDate(startDate);
            display.classList.remove('text-gray-400');
            display.classList.add('text-gray-700');
        } else if (startDate && endDate) {
            display.textContent = `${formatDate(startDate)} - ${formatDate(endDate)}`;
            display.classList.remove('text-gray-400');
            display.classList.add('text-gray-700');
        } else if (startDate) {
            display.textContent = `${formatDate(startDate)} - Select end date`;
            display.classList.remove('text-gray-400');
            display.classList.add('text-gray-700');
        } else {
            display.textContent = buttonText;
            display.classList.remove('text-gray-700');
            display.classList.add('text-gray-400');
        }
    };
    
    const selectDateFn = function(year, month, day) {
        const selectedDate = new Date(year, month, day);
        selectedDate.setHours(0, 0, 0, 0);
        
        if (singleDate) {
            state.startDate = selectedDate;
            state.endDate = null;
        } else {
            if (!state.startDate || selectedDate < state.startDate) {
                state.startDate = selectedDate;
                state.endDate = null;
            } else if (!state.endDate) {
                state.endDate = selectedDate;
            } else {
                state.startDate = selectedDate;
                state.endDate = null;
            }
        }
        
        updateDisplayFn();
        renderFn();
    };
    
    const changeMonthFn = function(delta) {
        state.currentMonth = new Date(state.currentMonth.getFullYear(), state.currentMonth.getMonth() + delta, 1);
        renderFn();
    };
    
    const setTodayFn = function() {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        state.startDate = today;
        state.endDate = singleDate ? null : today;
        updateDisplayFn();
        renderFn();
    };
    
    const clearFn = function() {
        state.startDate = null;
        state.endDate = null;
        updateDisplayFn();
        renderFn();
    };
    
    const applyFn = function() {
        if (!state.startDate) return;
        
        const finalStartDate = state.startDate;
        const finalEndDate = singleDate ? null : (state.endDate || state.startDate);
        
        const fromInput = document.getElementById(fromInputId);
        const toInput = document.getElementById(toInputId);
        
        const toLocalDateStr = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };
        if (fromInput) fromInput.value = toLocalDateStr(finalStartDate);
        if (toInput && !singleDate) toInput.value = toLocalDateStr(finalEndDate);
        
        const popup = document.getElementById(popupId);
        if (popup) {
            popup.style.opacity = '0';
            popup.style.transform = 'translateY(-8px)';
            setTimeout(() => {
                popup.classList.add('hidden');
                popup.style.opacity = '';
                popup.style.transform = '';
            }, 150);
        }
        state.isOpen = false;
        
        if (onApply) onApply();
    };
    
    const renderFn = function() {
        const popup = document.getElementById(popupId);
        if (!popup) return;
        
        const year = state.currentMonth.getFullYear();
        const month = state.currentMonth.getMonth();
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                           'July', 'August', 'September', 'October', 'November', 'December'];
        
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startDayOfWeek = firstDay.getDay();
        const adjustedStartDay = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
        
        const prevMonth = new Date(year, month, 0);
        const daysInPrevMonth = prevMonth.getDate();
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const isInRange = (dateObj) => {
            if (singleDate || !state.startDate || !state.endDate) return false;
            return dateObj >= state.startDate && dateObj <= state.endDate;
        };
        const isRangeStart = (dateObj) => state.startDate && dateObj.getTime() === state.startDate.getTime();
        const isRangeEnd = (dateObj) => state.endDate && dateObj.getTime() === state.endDate.getTime();
        
        let html = `
            <div class="flex items-center text-gray-900 mb-4">
                <button type="button" onclick="event.stopPropagation(); window.calendarInstances['${buttonId}'].changeMonth(-1)"
                        class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500">
                    <svg viewBox="0 0 20 20" fill="currentColor" class="size-5">
                        <path d="M11.78 5.22a.75.75 0 010 1.06L8.06 10l3.72 3.72a.75.75 0 11-1.06 1.06l-4.25-4.25a.75.75 0 010-1.06l4.25-4.25a.75.75 0 011.06 0z"/>
                    </svg>
                </button>
                <div class="flex-auto text-sm font-semibold text-center">${monthNames[month]} ${year}</div>
                <button type="button" onclick="event.stopPropagation(); window.calendarInstances['${buttonId}'].changeMonth(1)"
                        class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500">
                    <svg viewBox="0 0 20 20" fill="currentColor" class="size-5">
                        <path d="M8.22 5.22a.75.75 0 011.06 0l4.25 4.25a.75.75 0 010 1.06l-4.25 4.25a.75.75 0 01-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 010-1.06z"/>
                    </svg>
                </button>
            </div>
            <div class="grid grid-cols-7 text-xs text-center font-semibold text-gray-500 mb-2">
                <div>M</div><div>T</div><div>W</div><div>T</div><div>F</div><div>S</div><div>S</div>
            </div>
            <div class="grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200">
        `;
        
        for (let i = adjustedStartDay - 1; i >= 0; i--) {
            const day = daysInPrevMonth - i;
            html += `<button type="button" class="py-1.5 bg-gray-50 text-gray-400 hover:bg-gray-100"
                     onclick="event.stopPropagation(); window.calendarInstances['${buttonId}'].selectDate(${year}, ${month - 1}, ${day})">${day}</button>`;
        }
        
        for (let day = 1; day <= daysInMonth; day++) {
            const dateObj = new Date(year, month, day);
            dateObj.setHours(0, 0, 0, 0);
            
            const isToday = dateObj.getTime() === today.getTime();
            const isStart = isRangeStart(dateObj);
            const isEnd = isRangeEnd(dateObj);
            const inRange = isInRange(dateObj);
            
            let classes = 'py-1.5 bg-white hover:bg-gray-100';
            if (isStart || isEnd) {
                classes = 'py-1.5 font-semibold text-white bg-primary-600 hover:bg-primary-500';
            } else if (inRange) {
                classes = 'py-1.5 bg-primary-100 hover:bg-primary-200 text-primary-900';
            } else if (isToday) {
                classes += ' font-semibold text-primary-600';
            }
            
            html += `<button type="button" class="${classes}"
                     onclick="event.stopPropagation(); window.calendarInstances['${buttonId}'].selectDate(${year}, ${month}, ${day})">${day}</button>`;
        }
        
        const totalCells = adjustedStartDay + daysInMonth;
        const remainingCells = 42 - totalCells;
        for (let day = 1; day <= remainingCells && day <= 14; day++) {
            html += `<button type="button" class="py-1.5 bg-gray-50 text-gray-400 hover:bg-gray-100"
                     onclick="event.stopPropagation(); window.calendarInstances['${buttonId}'].selectDate(${year}, ${month + 1}, ${day})">${day}</button>`;
        }
        
        html += `</div>`;
        
        if (!singleDate) {
            html += `<div class="mt-3 text-xs text-center text-gray-500 mb-2">
                ${state.startDate && !state.endDate ? 'Select end date' : 'Click to select start date, then end date'}
            </div>`;
        }
        
        html += `
            <div class="flex gap-2 mt-3">
                <button type="button" onclick="event.stopPropagation(); window.calendarInstances['${buttonId}'].clear()"
                        class="flex-1 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">Clear</button>
                <button type="button" onclick="event.stopPropagation(); window.calendarInstances['${buttonId}'].setToday()"
                        class="flex-1 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">Today</button>
                <button type="button" onclick="event.stopPropagation(); window.calendarInstances['${buttonId}'].apply()"
                        class="flex-1 rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500">Apply</button>
            </div>
        `;
        
        popup.innerHTML = html;
    };
    
    document.addEventListener('click', function(event) {
        const popup = document.getElementById(popupId);
        const button = document.getElementById(buttonId);
        
        if (popup && state.isOpen && !popup.contains(event.target) && !button?.contains(event.target)) {
            popup.style.opacity = '0';
            popup.style.transform = 'translateY(-8px)';
            setTimeout(() => {
                popup.classList.add('hidden');
                popup.style.opacity = '';
                popup.style.transform = '';
            }, 150);
            state.isOpen = false;
        }
    });
    
    const instance = {
        toggle: toggleFn,
        updateDisplay: updateDisplayFn,
        selectDate: selectDateFn,
        changeMonth: changeMonthFn,
        setToday: setTodayFn,
        clear: clearFn,
        apply: applyFn,
        render: renderFn,
        state: state
    };
    
    if (!window.calendarInstances) window.calendarInstances = {};
    window.calendarInstances[buttonId] = instance;
    
    return instance;
}

// Make createRangePicker available globally
window.createRangePicker = createRangePicker;

// ============================================================================
// GLOBAL STATE
// ============================================================================

// Global state for table sorting
window.tableSortState = {
    accounts: { column: null, direction: 'asc' },
    users: { column: null, direction: 'asc' },
    locations: { column: null, direction: 'asc' },
    sensors: { column: null, direction: 'asc' },
    assets: { column: null, direction: 'asc' },
    groups: { column: null, direction: 'asc' }
};

// Infinite scroll state for users
window.usersInfiniteScroll = {
    currentPage: 1,
    pageSize: 100,  // Load 100 users per batch
    totalUsers: 0,
    isLoading: false,
    hasMore: true,
    allUsers: []  // Accumulate all loaded users
};

// ============================================================================
// INACTIVITY MANAGER
// ============================================================================

class InactivityManager {
    constructor() {
        this.timeout = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
        this.timer = null;
        this.activityEvents = [
            'mousedown',
            'mousemove', 
            'keypress',
            'scroll',
            'touchstart',
            'click'
        ];
    }
    
    start() {
        console.log('⏰ Starting inactivity timer (2 hour timeout)');
        this.resetTimer();
        
        // Listen for user activity
        this.activityEvents.forEach(event => {
            document.addEventListener(event, this.handleActivity.bind(this), true);
        });
    }
    
    stop() {
        console.log('⏰ Stopping inactivity timer');
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        
        // Remove activity listeners
        this.activityEvents.forEach(event => {
            document.removeEventListener(event, this.handleActivity.bind(this), true);
        });
    }
    
    handleActivity() {
        // Reset the timer on any user activity
        this.resetTimer();
        
        // Check if token needs refresh due to activity
        if (auth.shouldRefreshForActivity()) {
            console.log('🔄 Refreshing token due to user activity');
            auth.refreshToken().catch(error => {
                console.error('Failed to refresh token on activity:', error);
            });
        }
    }
    
    resetTimer() {
        if (this.timer) {
            clearTimeout(this.timer);
        }
        
        this.timer = setTimeout(() => {
            console.log('⏰ Inactivity timeout reached - logging out');
            autoLogout('You have been logged out due to inactivity.');
        }, this.timeout);
    }
}

// Create global inactivity manager instance
const inactivityManager = new InactivityManager();

// ============================================================================
// AUTHENTICATION FUNCTIONS
// ============================================================================

async function login(username, password) {
    try {
        const response = await fetch(`${API_BASE_URL}/auth/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ username, password })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Login failed');
        }

        const data = await response.json();
        auth.setToken(data.access_token);
        
        // Start inactivity timer after successful login
        inactivityManager.start();
        
        // Load user data
        await loadCurrentUser();
        
        return true;
    } catch (error) {
        console.error('Login error:', error);
        throw error;
    }
}

async function loginWithMicrosoft() {
    try {
        window.location.href = `${API_BASE_URL}/auth/microsoft`;
    } catch (error) {
        showError('Failed to initiate Microsoft login');
    }
}

async function loginWithGoogle() {
    try {
        window.location.href = `${API_BASE_URL}/auth/google`;
    } catch (error) {
        showError('Failed to initiate Google login');
    }
}

// logout function is now imported from utils/helpers.js

// ============================================================================
// LOGIN SCREEN
// ============================================================================

function showLoginScreen() {
    const content = document.getElementById('main-content');
    if (!content) {
        console.error('main-content element not found');
        return;
    }
    content.innerHTML = `
        <div class="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div class="max-w-md w-full space-y-8">
                <div>
                    <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        Sign in to Docospot
                    </h2>
                </div>
                <form class="mt-8 space-y-6" id="login-form">
                    <input type="hidden" name="remember" value="true">
                    <div class="rounded-md shadow-sm -space-y-px">
                        <div>
                            <label for="username" class="sr-only">Username</label>
                            <input id="username" name="username" type="text" required class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm" placeholder="Username">
                        </div>
                        <div>
                            <label for="password" class="sr-only">Password</label>
                            <input id="password" name="password" type="password" required class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm" placeholder="Password">
                        </div>
                    </div>

                    <div>
                        <button type="submit" class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                            Sign in
                        </button>
                    </div>
                    
                    <div class="relative">
                        <div class="absolute inset-0 flex items-center">
                            <div class="w-full border-t border-gray-300"></div>
                        </div>
                        <div class="relative flex justify-center text-sm">
                            <span class="px-2 bg-gray-50 text-gray-500">Or continue with</span>
                        </div>
                    </div>

                    <div class="grid grid-cols-2 gap-3">
                        <button type="button" id="microsoft-login" class="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 23 23">
                                <path d="M11.4 0H0v11.4h11.4V0z" fill="#f1511b"/>
                                <path d="M23 0H11.6v11.4H23V0z" fill="#80cc28"/>
                                <path d="M11.4 11.6H0V23h11.4V11.6z" fill="#00adef"/>
                                <path d="M23 11.6H11.6V23H23V11.6z" fill="#fbbc09"/>
                            </svg>
                            <span class="ml-2">Microsoft</span>
                        </button>
                        
                        <button type="button" id="google-login" class="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                            <svg class="w-5 h-5" viewBox="0 0 24 24">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            <span class="ml-2">Google</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    `;

    // Add event listeners
    document.getElementById('login-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const username = document.getElementById('username').value;
        const password = document.getElementById('password').value;

        try {
            await login(username, password);
            showSuccess('Login successful!');
            updateNavigation();
        } catch (error) {
            showError(error.message || 'Login failed');
        }
    });

    document.getElementById('microsoft-login').addEventListener('click', loginWithMicrosoft);
    document.getElementById('google-login').addEventListener('click', loginWithGoogle);
}

// ============================================================================
// WEBSOCKET EVENT HANDLERS
// ============================================================================

function handleTimeTrackingCheckIn(data) {
    console.log('🟢 Check-in event received:', data);
    showSuccess(`User checked in`);
    
    // Refresh time tracking data if currently visible, preserving active filters
    const timeTrackingTab = document.getElementById('time-tracking-tab');
    console.log('🔍 Looking for time-tracking-tab:', timeTrackingTab);
    if (timeTrackingTab && !timeTrackingTab.classList.contains('hidden')) {
        console.log('✅ Time tracking tab is visible, refreshing data...');
        loadTimeTrackingData(true);
    } else {
        console.log('⚠️ Time tracking tab not visible');
    }
}

function handleTimeTrackingCheckOut(data) {
    console.log('🔴 Check-out event received:', data);
    showSuccess(`User checked out (${data.duration_minutes} minutes)`);
    
    // Refresh time tracking data if currently visible, preserving active filters
    const timeTrackingTab = document.getElementById('time-tracking-tab');
    console.log('🔍 Looking for time-tracking-tab:', timeTrackingTab);
    if (timeTrackingTab && !timeTrackingTab.classList.contains('hidden')) {
        console.log('✅ Time tracking tab is visible, refreshing data...');
        loadTimeTrackingData(true);
    } else {
        console.log('⚠️ Time tracking tab not visible');
    }
}

function handleTimeTrackingSensorUpdate(data) {
    console.log('📡 Sensor update event received:', data);
    
    // Refresh time tracking data if currently visible, preserving active filters
    const timeTrackingTab = document.getElementById('time-tracking-tab');
    if (timeTrackingTab && !timeTrackingTab.classList.contains('hidden')) {
        console.log('✅ Time tracking tab is visible, refreshing data after sensor update...');
        loadTimeTrackingData(true);
    }
}

// ============================================================================
// APPLICATION INITIALIZATION
// ============================================================================

document.addEventListener('DOMContentLoaded', function() {
    console.log('DOMContentLoaded fired');
    console.log('Current path:', window.location.pathname);
    console.log('Auth token exists:', !!auth.token);
    
    // Check for OAuth token in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const error = urlParams.get('error');
    
    if (token) {
        // OAuth login successful
        auth.setToken(token);
        // Clean up URL
        const url = new URL(window.location);
        url.searchParams.delete('token');
        window.history.replaceState({}, document.title, url.pathname);
        
        // Start inactivity timer after OAuth login
        inactivityManager.start();
        
        showSuccess('Login successful!');
        
        // Load user data first, then show dashboard
        loadCurrentUser().then(() => {
            updateNavigation();
            showDashboard();
        }).catch((error) => {
            console.error('Failed to load user after OAuth:', error);
            showLoginScreen();
        });
    } else if (error) {
        // OAuth login failed
        let errorMessage = 'OAuth login failed';
        switch(error) {
            case 'oauth_failed':
                errorMessage = 'OAuth authentication failed. Please try again.';
                break;
            case 'no_email':
                errorMessage = 'No email address found in your account. Please ensure your account has an email.';
                break;
            case 'no_default_account':
                errorMessage = 'No default account configured. Please contact your administrator.';
                break;
            case 'oauth_processing_failed':
                errorMessage = 'Failed to process OAuth login. Please try again.';
                break;
        }
        // Clean up URL
        const url = new URL(window.location);
        url.searchParams.delete('error');
        window.history.replaceState({}, document.title, url.pathname);
        showError(errorMessage);
        updateNavigation();
    } else {
        // Normal initialization
        // Start inactivity timer if user is already logged in
        if (auth.isAuthenticated()) {
            inactivityManager.start();
            
            // Set up WebSocket event listeners BEFORE connecting
            wsClient.on('time_tracking_check_in', handleTimeTrackingCheckIn);
            wsClient.on('time_tracking_check_out', handleTimeTrackingCheckOut);
            wsClient.on('time_tracking_sensor_update', handleTimeTrackingSensorUpdate);
            
            // Connect to WebSocket for real-time updates
            wsClient.connect();
        }
        
        updateNavigation(); // This will automatically show login screen or dashboard based on auth status
    }
});

// ============================================================================
// GLOBAL EXPORTS FOR INLINE EVENT HANDLERS
// ============================================================================

// Make functions available globally for onclick handlers in HTML
window.autoLogout = autoLogout;
window.logout = logout;
window.showDashboard = showDashboard;
window.showAccounts = showAccounts;
window.showCreateAccountModal = showCreateAccountModal;
window.loadAccountsData = loadAccountsData;
window.editAccount = editAccount;
window.viewAccountUsersFiltered = viewAccountUsersFiltered;
window.createAccount = createAccount;
window.updateAccount = updateAccount;
window.toggleParentAccountTreeItem = toggleParentAccountTreeItem;
window.selectParentAccount = selectParentAccount;
window.toggleEditParentAccountTreeItem = toggleEditParentAccountTreeItem;
window.selectEditParentAccount = selectEditParentAccount;
window.toggleAccountItem = toggleAccountItem;
window.handleAccountSearchInput = handleAccountSearchInput;
window.handleAccountSearchFocus = handleAccountSearchFocus;
window.handleAccountSearchBlur = handleAccountSearchBlur;
window.selectAccountFromSearch = selectAccountFromSearch;
window.clearAccountSearch = clearAccountSearch;
window.showUsers = showUsers;
window.loadUsersData = loadUsersData;
window.clearFilters = clearFilters;
window.sortUsersTable = sortUsersTable;
window.showCreateUserModal = showCreateUserModal;
window.createUser = createUser;
window.editUser = editUser;
window.updateUser = updateUser;
window.deleteUser = deleteUser;
window.resendInvitation = resendInvitation;
window.toggleAccountTreeItem = toggleAccountTreeItem;
window.selectAccount = selectAccount;
window.selectEditAccount = selectEditAccount;
window.showLocations = showLocations;
window.loadLocationsData = loadLocationsData;
window.clearLocationsFilters = clearLocationsFilters;
window.toggleLocationsAccountFilterExpanded = toggleLocationsAccountFilterExpanded;
window.clearLocationsAccountFilter = clearLocationsAccountFilter;
window.toggleLocationsAccountFilterTreeItem = toggleLocationsAccountFilterTreeItem;
window.showCreateLocationModal = showCreateLocationModal;
window.createLocation = createLocation;
window.editLocation = editLocation;
window.updateLocation = updateLocation;
window.toggleLocationAccountTreeItem = toggleLocationAccountTreeItem;
window.selectLocationAccount = selectLocationAccount;
window.selectEditLocationAccount = selectEditLocationAccount;
window.showAssets = showAssets;
window.showTimeTracking = showTimeTracking;  // Main function
window.showTimeTrackingTab = showTimeTrackingTab;  // Tab switcher
window.showSensors = showSensors;
window.showSetupSensors = showSetupSensors;
window.showAlarms = showAlarms;
window.showAlarmTemplates = showAlarmTemplates;
window.showSetupAlarms = showSetupAlarms;
window.showReports = showReports;
window.showReportTemplates = showReportTemplates;
window.showSettings = showSettings;
window.showMyLocations = showMyLocations;
window.showProfile = showProfile;
window.showCategories = showCategories;
window.showAnalytics = showAnalytics;
window.showFavorites = showFavorites;
window.showAssets = showAssets;
window.showGroups = showGroups;
window.showTasks = showTasks;
window.switchTasksTab = switchTasksTab;
window.showMyAssets = showMyAssets;
window.showAssetInsightsTab = showAssetInsightsTab;
window.showMyLocations = showMyLocations;
window.showMyLocationsTab = showMyLocationsTab;
window.toggleSetupMenu = toggleSetupMenu;
window.toggleAccountItem = toggleAccountItem;
window.toggleDashboardAccountItem = toggleDashboardAccountItem;
window.logout = logout;
window.updateNavigation = updateNavigation;
window.showLoginScreen = showLoginScreen;
window.setLanguage = setLanguage;

// ============================================================================
// NAVIGATION WRAPPER WITH UNSAVED CHANGES CHECK
// ============================================================================

// Store original navigation functions
const originalNavFunctions = {
    showDashboard,
    showTimeTracking,
    showMyLocations,
    showMyAssets,
    showAlarms,
    showReports,
    showAccounts,
    showUsers,
    showLocations,
    showAssets,
    showGroups,
    showSettings,
    showSetupAlarms,
    showSetupSensors,
    showTasks,
    showCategories,
    showAnalytics,
    showFavorites,
    showProfile
};

// Create wrapped navigation functions that check for unsaved changes
async function wrapNavigationFunction(originalFn, fnName) {
    // Check for unsaved template changes
    if (window.checkTemplateUnsavedChanges) {
        const canProceed = await window.checkTemplateUnsavedChanges();
        if (!canProceed) {
            console.log(`Navigation to ${fnName} cancelled due to unsaved changes`);
            return;
        }
    }
    // Proceed with navigation
    originalFn();
}

// Override window functions with wrapped versions
window.showDashboard = () => wrapNavigationFunction(originalNavFunctions.showDashboard, 'Dashboard');
window.showTimeTracking = () => wrapNavigationFunction(originalNavFunctions.showTimeTracking, 'Time Tracking');
window.showMyLocations = () => wrapNavigationFunction(originalNavFunctions.showMyLocations, 'My Locations');
window.showMyAssets = () => wrapNavigationFunction(originalNavFunctions.showMyAssets, 'My Assets');
window.showAlarms = () => wrapNavigationFunction(originalNavFunctions.showAlarms, 'Alarms');
window.showReports = () => wrapNavigationFunction(originalNavFunctions.showReports, 'Reports');
window.showAccounts = () => wrapNavigationFunction(originalNavFunctions.showAccounts, 'Accounts');
window.showUsers = () => wrapNavigationFunction(originalNavFunctions.showUsers, 'Users');
window.showLocations = () => wrapNavigationFunction(originalNavFunctions.showLocations, 'Locations');
window.showAssets = () => wrapNavigationFunction(originalNavFunctions.showAssets, 'Assets');
window.showGroups = () => wrapNavigationFunction(originalNavFunctions.showGroups, 'Groups');
window.showSettings = () => wrapNavigationFunction(originalNavFunctions.showSettings, 'Settings');
window.showSetupAlarms = () => wrapNavigationFunction(originalNavFunctions.showSetupAlarms, 'Setup Alarms');
window.showSetupSensors = () => wrapNavigationFunction(originalNavFunctions.showSetupSensors, 'Setup Sensors');
window.showTasks = () => wrapNavigationFunction(originalNavFunctions.showTasks, 'Tasks');
window.showCategories = () => wrapNavigationFunction(originalNavFunctions.showCategories, 'Categories');
window.showAnalytics = () => wrapNavigationFunction(originalNavFunctions.showAnalytics, 'Analytics');
window.showFavorites = () => wrapNavigationFunction(originalNavFunctions.showFavorites, 'Favorites');
window.showProfile = () => wrapNavigationFunction(originalNavFunctions.showProfile, 'Profile');

console.log('✅ app.js loaded successfully!');
